import { PlusIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { MonthLockedBadge } from '@/components/monthly/MonthLockedBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useActiveMachinesForFilterQuery,
  useActiveSitesForFilterQuery,
} from '@/hooks/useReferenceDataQueries';
import { ApiError } from '@/lib/api/error';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatMachineName } from '@/lib/utils/machine-label';
import { listLogsheetsMakerRequest } from '@/services/logsheets-maker';
import type {
  LogsheetMakerListItem,
  LogsheetMakerListQuery,
  LogsheetStatus,
} from '@/types/logsheets-maker';
import type { PaginatedResult } from '@/types/roles';

const PAGE_SIZE = 20;

const STATUS_OPTIONS: readonly LogsheetStatus[] = [
  'Draft',
  'Submitted',
  'Checked',
  'Approved',
  'Rejected',
];

function isValidStatus(s: string | null): s is LogsheetStatus {
  return (
    s === 'Draft' || s === 'Submitted' || s === 'Checked' || s === 'Approved' || s === 'Rejected'
  );
}

function parsePositiveInt(s: string | null): number | undefined {
  if (s === null) return undefined;
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function statusVariant(s: LogsheetStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (s === 'Rejected') return 'destructive';
  if (s === 'Approved') return 'default';
  if (s === 'Checked') return 'default';
  if (s === 'Submitted') return 'secondary';
  return 'outline'; // Draft
}

export function MakerLogsheetsListPage(): JSX.Element {
  const { permissions } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQ = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status');
  const initialStatus: LogsheetStatus | undefined = isValidStatus(statusParam)
    ? statusParam
    : undefined;
  const initialFromDate = searchParams.get('fromDate') ?? '';
  const initialToDate = searchParams.get('toDate') ?? '';
  const initialSiteId = parsePositiveInt(searchParams.get('siteId'));
  const initialMachineId = parsePositiveInt(searchParams.get('machineId'));
  const initialPage = (() => {
    const p = Number.parseInt(searchParams.get('page') ?? '1', 10);
    return Number.isFinite(p) && p > 0 ? p : 1;
  })();

  const [q, setQ] = useState<string>(initialQ);
  const [debouncedQ, setDebouncedQ] = useState<string>(initialQ);
  const [status, setStatus] = useState<LogsheetStatus | undefined>(initialStatus);
  const [fromDate, setFromDate] = useState<string>(initialFromDate);
  const [toDate, setToDate] = useState<string>(initialToDate);
  const [siteId, setSiteId] = useState<number | undefined>(initialSiteId);
  const [machineId, setMachineId] = useState<number | undefined>(initialMachineId);
  const [page, setPage] = useState<number>(initialPage);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedQ(q);
    }, 300);
    return () => {
      window.clearTimeout(t);
    };
  }, [q]);

  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedQ !== '') next.set('q', debouncedQ);
    if (status !== undefined) next.set('status', status);
    if (fromDate !== '') next.set('fromDate', fromDate);
    if (toDate !== '') next.set('toDate', toDate);
    if (siteId !== undefined) next.set('siteId', String(siteId));
    if (machineId !== undefined) next.set('machineId', String(machineId));
    if (page !== 1) next.set('page', String(page));
    setSearchParams(next, { replace: true });
  }, [debouncedQ, status, fromDate, toDate, siteId, machineId, page, setSearchParams]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, status, fromDate, toDate, siteId, machineId]);

  const listQueryArgs: LogsheetMakerListQuery = useMemo(() => {
    const out: { -readonly [K in keyof LogsheetMakerListQuery]: LogsheetMakerListQuery[K] } = {
      q: debouncedQ,
      page,
      pageSize: PAGE_SIZE,
    };
    if (status !== undefined) out.status = status;
    if (fromDate !== '') out.fromDate = fromDate;
    if (toDate !== '') out.toDate = toDate;
    if (siteId !== undefined) out.siteId = siteId;
    if (machineId !== undefined) out.machineId = machineId;
    return out;
  }, [debouncedQ, status, fromDate, toDate, siteId, machineId, page]);

  const listQuery = useQuery<PaginatedResult<LogsheetMakerListItem>, ApiError>({
    queryKey: ['logsheets-maker', listQueryArgs],
    queryFn: () => listLogsheetsMakerRequest(listQueryArgs),
    staleTime: 30_000,
  });

  // REVIEW FP-004: shared 5-minute-stale reference-data hooks. Replaces the
  // ad-hoc useQuery pair that ran at default 30s staleTime.
  const sitesQuery = useActiveSitesForFilterQuery();
  const machinesQuery = useActiveMachinesForFilterQuery();

  const columns = useMemo<ColumnDef<LogsheetMakerListItem>[]>(
    () => [
      {
        accessorKey: 'entryDate',
        header: 'Date',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.entryDate}</span>,
      },
      {
        accessorKey: 'siteName',
        header: 'Site',
        cell: ({ row }) => <span>{row.original.siteName}</span>,
      },
      {
        accessorKey: 'machineName',
        header: 'Machine',
        cell: ({ row }) => (
          <span>
            {formatMachineName(
              row.original.machineName,
              row.original.vehicleNumber,
              row.original.machineCode,
            )}
          </span>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
          <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>
            {row.original.type}
          </Badge>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
        ),
      },
      {
        accessorKey: 'updatedAt',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="font-mono text-xs">
            {new Date(row.original.updatedAt).toLocaleDateString()}
          </span>
        ),
      },
      // Phase 15 Plan 15-02 (D-138): Month lock badge + disabled state for Edit/Delete.
      {
        id: 'monthLocked',
        header: 'Month',
        cell: ({ row }) =>
          row.original.monthLocked === true ? (
            <span data-testid={`maker-logsheet-row-${String(row.original.id)}-month-locked-badge`}>
              <MonthLockedBadge locked={true} />
            </span>
          ) : null,
      },
    ],
    [],
  );

  const data = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as LogsheetMakerListItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!permissions.includes('logsheet.maker.read')) {
    return <Navigate to="/dashboard" replace />;
  }

  const canCreate = permissions.includes('logsheet.maker.create');

  return (
    <div className="space-y-6" data-testid="maker-logsheets-list-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">My Logsheets</h1>
          <p className="text-muted-foreground text-sm">
            Your Work and Idle entries — drafts, submissions, and rejections.
          </p>
        </div>
        {canCreate && (
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <Button
              asChild
              data-testid="maker-logsheets-add-button"
              className="w-full sm:w-auto sm:shrink-0"
            >
              <Link to="/logsheets/maker/new">
                <PlusIcon size={16} weight="bold" /> New Entry
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              data-testid="maker-list-bulk-create-button"
              className="w-full sm:w-auto sm:shrink-0"
            >
              <Link to="/logsheets/maker/bulk-create">
                <PlusIcon size={16} weight="bold" /> Bulk Create
              </Link>
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All entries</CardTitle>
          <CardDescription>
            Search by machine code or name. Filter by status, date range, site, or machine.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters — wrap on mobile */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[160px] space-y-1">
              <Label htmlFor="maker-logsheets-search">Search</Label>
              <Input
                id="maker-logsheets-search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                }}
                placeholder="Machine code or name…"
                data-testid="maker-logsheets-search-input"
              />
            </div>
            <div className="w-full sm:w-44 space-y-1">
              <Label htmlFor="maker-logsheets-status">Status</Label>
              <Select
                value={status ?? 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setStatus(undefined);
                  } else if (isValidStatus(v)) {
                    setStatus(v);
                  }
                }}
              >
                <SelectTrigger
                  id="maker-logsheets-status"
                  data-testid="maker-logsheets-status-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="maker-logsheets-status-all">
                    All
                  </SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`maker-logsheets-status-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-40 space-y-1">
              <Label htmlFor="maker-logsheets-from-date">From date</Label>
              <Input
                id="maker-logsheets-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                }}
                data-testid="maker-logsheets-from-date"
              />
            </div>
            <div className="w-full sm:w-40 space-y-1">
              <Label htmlFor="maker-logsheets-to-date">To date</Label>
              <Input
                id="maker-logsheets-to-date"
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                }}
                data-testid="maker-logsheets-to-date"
              />
            </div>
            <div className="w-full sm:w-40 space-y-1">
              <Label htmlFor="maker-logsheets-site">Site</Label>
              <Select
                value={siteId !== undefined ? String(siteId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setSiteId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setSiteId(n);
                  }
                }}
              >
                <SelectTrigger id="maker-logsheets-site" data-testid="maker-logsheets-site-filter">
                  <SelectValue placeholder="All sites" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="maker-logsheets-site-all">
                    All sites
                  </SelectItem>
                  {(sitesQuery.data?.items ?? []).map((s) => (
                    <SelectItem
                      key={s.id}
                      value={String(s.id)}
                      data-testid={`maker-logsheets-site-option-${String(s.id)}`}
                    >
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-full sm:w-44 space-y-1">
              <Label htmlFor="maker-logsheets-machine">Machine</Label>
              <Select
                value={machineId !== undefined ? String(machineId) : 'all'}
                onValueChange={(v) => {
                  if (v === 'all') {
                    setMachineId(undefined);
                  } else {
                    const n = Number(v);
                    if (Number.isFinite(n) && n > 0) setMachineId(n);
                  }
                }}
              >
                <SelectTrigger
                  id="maker-logsheets-machine"
                  data-testid="maker-logsheets-machine-filter"
                >
                  <SelectValue placeholder="All machines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="maker-logsheets-machine-all">
                    All machines
                  </SelectItem>
                  {(machinesQuery.data?.items ?? []).map((m) => (
                    <SelectItem
                      key={m.id}
                      value={String(m.id)}
                      data-testid={`maker-logsheets-machine-option-${String(m.id)}`}
                    >
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {listQuery.isLoading && (
            <p className="text-sm text-muted-foreground" data-testid="maker-logsheets-loading">
              Loading logsheets…
            </p>
          )}
          {listQuery.isError && (
            <p className="text-sm text-destructive" data-testid="maker-logsheets-error">
              Failed to load logsheets.
            </p>
          )}
          {listQuery.data && (
            <>
              <div data-testid="maker-logsheets-table">
                {/* Mobile card list — hidden on md+ */}
                <div className="md:hidden space-y-3">
                  {table.getRowModel().rows.length === 0 ? (
                    <p className="text-sm text-center py-8 text-muted-foreground" data-testid="maker-logsheets-empty">
                      No logsheet entries.
                    </p>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        onClick={() => {
                          void navigate(`/logsheets/view/${String(row.original.id)}`);
                        }}
                        className="w-full text-left rounded-lg border p-4 shadow-sm hover:bg-muted/40 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div>
                            <div className="font-medium text-sm">
                              {formatMachineName(row.original.machineName, row.original.vehicleNumber)}
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">{row.original.machineCode}</div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <Badge variant={statusVariant(row.original.status)}>{row.original.status}</Badge>
                            <Badge variant={row.original.type === 'Idle' ? 'secondary' : 'default'}>{row.original.type}</Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                          <div>
                            <span className="font-medium text-foreground">Date: </span>
                            <span className="font-mono">{row.original.entryDate}</span>
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Site: </span>
                            {row.original.siteName}
                          </div>
                          <div>
                            <span className="font-medium text-foreground">Updated: </span>
                            <span className="font-mono">{new Date(row.original.updatedAt).toLocaleDateString()}</span>
                          </div>
                          {row.original.monthLocked === true && (
                            <div className="col-span-2">
                              <MonthLockedBadge locked={true} />
                            </div>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>

                {/* Desktop table — hidden on mobile */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      {table.getHeaderGroups().map((hg) => (
                        <TableRow key={hg.id}>
                          {hg.headers.map((h) => (
                            <TableHead key={h.id}>
                              {h.isPlaceholder
                                ? null
                                : flexRender(h.column.columnDef.header, h.getContext())}
                            </TableHead>
                          ))}
                        </TableRow>
                      ))}
                    </TableHeader>
                    <TableBody>
                      {table.getRowModel().rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={columns.length} className="text-center py-8">
                            <span data-testid="maker-logsheets-empty">No logsheet entries.</span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        table.getRowModel().rows.map((row) => (
                          <TableRow
                            key={row.id}
                            data-testid={`maker-logsheets-row-${String(row.original.id)}`}
                            onClick={() => {
                              void navigate(`/logsheets/view/${String(row.original.id)}`);
                            }}
                            className="cursor-pointer hover:bg-muted/40"
                          >
                            {row.getVisibleCells().map((cell) => (
                              <TableCell key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <p
                  className="text-xs text-muted-foreground"
                  data-testid="maker-logsheets-pagination-summary"
                >
                  Page {page} of {pageCount} • {total} total
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.max(1, p - 1));
                    }}
                    disabled={page <= 1}
                    data-testid="maker-logsheets-pagination-prev"
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setPage((p) => Math.min(pageCount, p + 1));
                    }}
                    disabled={page >= pageCount}
                    data-testid="maker-logsheets-pagination-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
